import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ShieldCheck, Lock, Globe, Volume2, VolumeX, Phone, ArrowLeft, CheckCircle, Crown, ChevronDown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isAuthenticated } = useAuth();
  const { t, language, setLanguage, supportedLanguages, speak, stopSpeaking } = useLanguage();
  
  // Payment verification state
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paidPlan, setPaidPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Simplified flow: phone -> OTP -> done (auto-register if new user)
  const [step, setStep] = useState('phone'); // 'phone', 'verify', 'register', or 'newuser'
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [demoOtp, setDemoOtp] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [sessionId, setSessionId] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    otp: ''
  });

  // Check for payment success on page load
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const plan = searchParams.get('plan');
    const sessionId = searchParams.get('session_id');
    const existingUser = searchParams.get('existing');
    
    // Allow existing subscribers to bypass payment check
    if (existingUser === 'true') {
      setPaymentVerified(true);
      setPaidPlan('existing');
      return;
    }
    
    if (paymentSuccess === 'true' && sessionId) {
      setCheckingPayment(true);
      verifyPayment(sessionId, plan);
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId, plan) => {
    try {
      const response = await axios.get(`${API_URL}/payments/status/${sessionId}`);
      
      if (response.data.is_paid) {
        setPaymentVerified(true);
        setPaidPlan(plan);
        // Store session_id for registration
        localStorage.setItem('pendingSubscription', JSON.stringify({
          sessionId,
          plan,
          verifiedAt: new Date().toISOString()
        }));
        toast.success(language === 'hi' 
          ? 'भुगतान सफल! अपना अकाउंट बनाएं।' 
          : 'Payment confirmed! Create your account below.');
        // Show registration form
        setStep('register');
        // Clean URL
        window.history.replaceState({}, '', '/login');
      } else {
        toast.error(language === 'hi' 
          ? 'भुगतान अधूरा है। कृपया पुनः प्रयास करें।'
          : 'Payment not completed. Please try again.');
        navigate('/pricing');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error(language === 'hi'
        ? 'भुगतान सत्यापित नहीं हो सका। कृपया सहायता से संपर्क करें।'
        : 'Could not verify payment. Please contact support.');
    } finally {
      setCheckingPayment(false);
    }
  };

  // Complete registration for new paid users
  const handleCompleteRegistration = async () => {
    if (!formData.name || formData.name.length < 2) {
      toast.error(language === 'hi' ? 'कृपया अपना नाम दर्ज करें' : 'Please enter your name');
      return;
    }
    if (!formData.phone || formData.phone.length < 10) {
      toast.error(language === 'hi' ? '10 अंकों का फ़ोन नंबर दर्ज करें' : 'Enter 10-digit phone number');
      return;
    }
    
    // Get session ID from localStorage
    const storedSession = localStorage.getItem('pendingSubscription');
    const regSessionId = storedSession ? JSON.parse(storedSession).sessionId : null;
    
    if (!regSessionId) {
      toast.error(language === 'hi' 
        ? 'भुगतान सत्र नहीं मिला। कृपया पुनः भुगतान करें।'
        : 'Payment session not found. Please make payment again.');
      navigate('/pricing');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/complete-registration`, {
        session_id: regSessionId,
        phone: formData.phone,
        name: formData.name
      });
      
      toast.success(language === 'hi' 
        ? 'खाता बन गया! OTP भेज दिया गया।' 
        : 'Account created! OTP sent.');
      
      // Move to OTP verification
      setStep('verify');
      setCountdown(300);
      if (response.data.demo_otp) setDemoOtp(response.data.demo_otp);
      
      // Clear pending subscription
      localStorage.removeItem('pendingSubscription');
      
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already registered')) {
        toast.error(language === 'hi'
          ? 'यह नंबर पहले से रजिस्टर्ड है। कृपया लॉगिन करें।'
          : 'This number is already registered. Please login.');
        setPaymentVerified(false);
        setStep('phone');
      } else {
        toast.error(error.response?.data?.detail || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => stopSpeaking();
  }, [stopSpeaking]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Format phone number for display
  const formatPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length <= 10) return cleaned;
    return cleaned;
  };

  // Step 1: Request OTP - checks if user exists
  const handleRequestOtp = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      toast.error(language === 'hi' ? '10 अंकों का फ़ोन नंबर दर्ज करें' : 'Enter 10-digit phone number');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/request-otp`, { phone: formData.phone });
      toast.success(language === 'hi' ? 'OTP भेज दिया गया!' : 'OTP sent!');
      setStep('verify');
      setCountdown(300);
      setIsNewUser(response.data.is_new_user || false);
      if (response.data.demo_otp) setDemoOtp(response.data.demo_otp);
    } catch (error) {
      // Handle "user not found" - redirect to subscription
      if (error.response?.status === 404) {
        toast.error(
          language === 'hi' 
            ? 'यह नंबर रजिस्टर्ड नहीं है। कृपया पहले सब्सक्राइब करें।' 
            : 'No account found. Please subscribe first to create an account.'
        );
        // Redirect to pricing page after a short delay
        setTimeout(() => navigate('/pricing'), 2000);
      } else if (error.response?.status === 429) {
        toast.error(
          language === 'hi'
            ? 'बहुत सारे अनुरोध। कृपया कुछ देर बाद प्रयास करें।'
            : 'Too many requests. Please wait before trying again.'
        );
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send OTP');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP - if new user, ask for name
  const handleVerifyOtp = async () => {
    if (!formData.otp || formData.otp.length !== 6) {
      toast.error(language === 'hi' ? '6 अंकों का OTP दर्ज करें' : 'Enter 6-digit OTP');
      return;
    }
    
    // If new user, we need their name first
    if (isNewUser && !formData.name) {
      setStep('newuser');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/auth/verify-otp?identifier=${encodeURIComponent(formData.phone)}&otp=${formData.otp}${isNewUser ? `&name=${encodeURIComponent(formData.name)}` : ''}`
      );
      
      localStorage.setItem('sehatSetu_token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      toast.success(language === 'hi' ? (isNewUser ? 'स्वागत है!' : 'लॉगिन सफल!') : (isNewUser ? 'Welcome!' : 'Login successful!'));
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpeak = () => {
    if (isSpeaking) {
      stopSpeaking();
      setIsSpeaking(false);
    } else {
      speak(language === 'hi' ? 'अपना फ़ोन नंबर डालें और OTP से लॉगिन करें' : 'Enter your phone number and login with OTP');
      setIsSpeaking(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Clean Header */}
      <header className="secure-header text-white p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8" />
            <div>
              <h1 className="text-xl font-bold font-['Manrope']">{t('appName')}</h1>
              <p className="text-xs opacity-80">{t('appTagline')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={handleSpeak} className="text-white hover:bg-white/20">
              {isSpeaking ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </Button>
            
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-white hover:bg-white/20">
                  <Globe className="h-5 w-5 mr-2" />
                  {supportedLanguages.find(l => l.code === language)?.flag} {supportedLanguages.find(l => l.code === language)?.name || language}
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {supportedLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-slate-100 font-semibold' : ''}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                    {language === lang.code && <CheckCircle className="h-4 w-4 ml-auto text-green-500" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          {/* Payment Checking State */}
          {checkingPayment && (
            <Card className="shadow-xl border-0 rounded-2xl overflow-hidden">
              <CardContent className="p-8 text-center">
                <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-slate-600">Verifying your payment...</p>
              </CardContent>
            </Card>
          )}
          
          {/* Payment Not Made - Redirect to Pricing */}
          {!checkingPayment && !paymentVerified && !localStorage.getItem('pendingSubscription') && (
            <Card className="shadow-xl border-0 rounded-2xl overflow-hidden">
              <CardContent className="p-8 text-center">
                <Crown className="h-16 w-16 mx-auto text-amber-500 mb-4" />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Choose Your Plan First</h2>
                <p className="text-slate-600 mb-6">
                  Select a subscription plan to get started with your Health Vault.
                </p>
                <Button 
                  onClick={() => navigate('/')}
                  className="w-full h-12 bg-[#0D2340] hover:bg-[#0D2340]/90 text-white"
                >
                  View Pricing Plans
                </Button>
                <p className="text-xs text-slate-500 mt-4">
                  Includes: Family profiles, Health diary, Emergency card & more
                </p>
                
                {/* Link for existing users */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-2">Already have an account?</p>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/login?existing=true')}
                    className="w-full"
                    data-testid="existing-user-login-btn"
                  >
                    Login to Existing Account
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Payment Verified OR Has Pending Subscription - Show Login Form */}
          {!checkingPayment && (paymentVerified || localStorage.getItem('pendingSubscription')) && (
          <Card className="shadow-xl border-0 rounded-2xl overflow-hidden">
            {/* Payment Success Banner */}
            {(paymentVerified || localStorage.getItem('pendingSubscription')) && (
              <div className="bg-green-50 border-b border-green-200 p-3">
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Payment Confirmed!</span>
                  {paidPlan && (
                    <Badge className="bg-green-600 text-white ml-2">
                      {paidPlan.charAt(0).toUpperCase() + paidPlan.slice(1)} Plan
                    </Badge>
                  )}
                </div>
              </div>
            )}
            
            <CardHeader className="text-center pb-2 pt-6">
              <CardTitle className="text-2xl font-['Manrope'] text-[#0D2340]">
                {step === 'newuser' 
                  ? (language === 'hi' ? 'आपका स्वागत है!' : 'Welcome!')
                  : paidPlan === 'existing'
                    ? (language === 'hi' ? 'वापस स्वागत है!' : 'Welcome Back!')
                    : (language === 'hi' ? 'खाता बनाएं' : 'Create Your Account')}
              </CardTitle>
              <CardDescription className="text-base">
                {paidPlan === 'existing'
                  ? (language === 'hi' ? 'अपने फ़ोन नंबर से लॉगिन करें' : 'Login with your phone number')
                  : (language === 'hi' ? 'अपनी स्वास्थ्य तिजोरी' : 'Your Health Vault awaits')}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4 px-6 pb-8">
              
              {/* REGISTRATION STEP: Name + Phone (for new paid users) */}
              {step === 'register' && (
                <div className="space-y-5 animate-fade-in">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl text-center">
                    <CheckCircle className="h-10 w-10 mx-auto text-green-600 mb-2" />
                    <p className="text-green-800 font-medium">
                      {language === 'hi' ? 'अब अपना खाता बनाएं' : 'Now create your account'}
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      {language === 'hi' ? 'बस आपका नाम और फ़ोन नंबर' : 'Just your name and phone number'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium text-slate-700">
                      {language === 'hi' ? 'आपका नाम' : 'Your Name'}
                    </Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-14 text-xl mt-2 rounded-xl border-slate-200 focus:border-green-500 focus:ring-green-500"
                      placeholder={language === 'hi' ? 'जैसे राहुल शर्मा' : 'e.g. Rahul Sharma'}
                      data-testid="register-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium text-slate-700">
                      {language === 'hi' ? 'फ़ोन नंबर' : 'Phone Number'}
                    </Label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                      className="h-14 text-xl mt-2 rounded-xl border-slate-200 focus:border-green-500 focus:ring-green-500"
                      placeholder={language === 'hi' ? '10 अंक जैसे 98765 43210' : '10 digits e.g. 98765 43210'}
                      data-testid="register-phone-input"
                      maxLength={10}
                    />
                  </div>
                  
                  <Button
                    onClick={handleCompleteRegistration}
                    className="w-full h-14 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl shadow-lg"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading 
                      ? (language === 'hi' ? 'खाता बना रहे हैं...' : 'Creating Account...') 
                      : (language === 'hi' ? 'खाता बनाएं और OTP प्राप्त करें' : 'Create Account & Get OTP')}
                  </Button>
                </div>
              )}
              
              {/* STEP 1: Phone Number (for existing users only) */}
              {step === 'phone' && (
                <div className="space-y-5 animate-fade-in">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl text-center">
                    <Phone className="h-10 w-10 mx-auto text-green-600 mb-2" />
                    <p className="text-green-800 font-medium">
                      {language === 'hi' ? 'फ़ोन नंबर से शुरू करें' : 'Start with your phone'}
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      {language === 'hi' ? 'कोई पासवर्ड नहीं, बस OTP' : 'No password needed, just OTP'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium text-slate-700">
                      {language === 'hi' ? 'फ़ोन नंबर' : 'Phone Number'}
                    </Label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                      className="h-14 text-xl mt-2 rounded-xl border-slate-200 focus:border-green-500 focus:ring-green-500"
                      placeholder={language === 'hi' ? '10 अंक जैसे 98765 43210' : '10 digits e.g. 98765 43210'}
                      data-testid="phone-input"
                      maxLength={10}
                    />
                  </div>
                  
                  <Button
                    onClick={handleRequestOtp}
                    disabled={isLoading || formData.phone.length < 10}
                    className="w-full h-14 text-lg bg-[#2E7D32] hover:bg-[#256029] rounded-xl font-medium transition-all duration-200 shadow-lg shadow-green-500/20"
                    data-testid="request-otp-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        {language === 'hi' ? 'भेज रहे हैं...' : 'Sending...'}
                      </span>
                    ) : (
                      language === 'hi' ? 'OTP भेजें' : 'Send OTP'
                    )}
                  </Button>
                </div>
              )}

              {/* STEP 2: OTP Verification */}
              {step === 'verify' && (
                <div className="space-y-4 animate-fade-in">
                  <Button
                    variant="ghost"
                    onClick={() => { setStep('phone'); setDemoOtp(''); setFormData({...formData, otp: ''}); }}
                    className="text-slate-500 hover:text-slate-700 -ml-2"
                    size="sm"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    {language === 'hi' ? 'वापस' : 'Back'}
                  </Button>
                  
                  <div className="text-center py-2">
                    <p className="text-slate-600">
                      {language === 'hi' ? 'OTP भेजा गया' : 'OTP sent to'} <span className="font-semibold text-slate-800">{formData.phone}</span>
                    </p>
                  </div>
                  
                  {/* Demo OTP Display */}
                  {demoOtp && (
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl text-center">
                      <p className="text-xs text-amber-700 mb-1">{language === 'hi' ? 'डेमो - आपका OTP:' : 'Demo - Your OTP:'}</p>
                      <p className="text-3xl font-bold text-amber-800 tracking-[0.3em] font-mono">{demoOtp}</p>
                      <p className="text-xs text-amber-600 mt-1">{language === 'hi' ? '(प्रोडक्शन में SMS आएगा)' : '(Would be SMS in production)'}</p>
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-base font-medium text-slate-700">
                      {language === 'hi' ? 'OTP दर्ज करें' : 'Enter OTP'}
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={formData.otp}
                      onChange={(e) => setFormData({ ...formData, otp: e.target.value.replace(/\D/g, '') })}
                      className="h-16 text-3xl text-center tracking-[0.5em] font-mono mt-2 rounded-xl border-slate-200 focus:border-green-500"
                      placeholder={language === 'hi' ? '6 अंक' : '6 digits'}
                      data-testid="otp-input"
                    />
                    {countdown > 0 && (
                      <p className="text-sm text-slate-500 text-center mt-2">
                        {language === 'hi' ? `समय शेष: ${formatTime(countdown)}` : `Time remaining: ${formatTime(countdown)}`}
                      </p>
                    )}
                  </div>
                  
                  <Button
                    onClick={handleVerifyOtp}
                    disabled={isLoading || formData.otp.length !== 6}
                    className="w-full h-14 text-lg bg-[#2E7D32] hover:bg-[#256029] rounded-xl font-medium transition-all duration-200 shadow-lg shadow-green-500/20"
                    data-testid="verify-otp-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        {language === 'hi' ? 'जांच रहे हैं...' : 'Verifying...'}
                      </span>
                    ) : (
                      language === 'hi' ? 'आगे बढ़ें' : 'Continue'
                    )}
                  </Button>
                </div>
              )}

              {/* STEP 3: New User - Just Name */}
              {step === 'newuser' && (
                <div className="space-y-5 animate-fade-in">
                  <Button
                    variant="ghost"
                    onClick={() => setStep('verify')}
                    className="text-slate-500 hover:text-slate-700 -ml-2"
                    size="sm"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    {language === 'hi' ? 'वापस' : 'Back'}
                  </Button>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl text-center">
                    <p className="text-blue-800 font-medium text-lg">
                      {language === 'hi' ? '🎉 नया अकाउंट बनाएं' : '🎉 Create your account'}
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      {language === 'hi' ? 'बस एक और कदम' : 'Just one more step'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium text-slate-700">
                      {language === 'hi' ? 'आपका नाम' : 'Your Name'}
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-14 text-lg mt-2 rounded-xl border-slate-200 focus:border-blue-500"
                      placeholder={language === 'hi' ? 'जैसे राहुल शर्मा' : 'e.g. Rahul Sharma'}
                      data-testid="name-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleCompleteRegistration}
                    disabled={isLoading || !formData.name.trim()}
                    className="w-full h-14 text-lg bg-[#0D2340] hover:bg-[#151c66] rounded-xl font-medium transition-all duration-200 shadow-lg shadow-indigo-500/20"
                    data-testid="complete-registration-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        {language === 'hi' ? 'बना रहे हैं...' : 'Creating...'}
                      </span>
                    ) : (
                      language === 'hi' ? 'अकाउंट बनाएं' : 'Create Account'
                    )}
                  </Button>
                </div>
              )}

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500 pt-4 border-t border-slate-100 mt-6">
                <Lock className="h-4 w-4" />
                <span className="blockchain-badge">
                  <ShieldCheck className="h-3 w-3" />
                  {t('blockchainSecured')}
                </span>
              </div>
            </CardContent>
          </Card>
          )}
        </div>
      </main>

      <footer className="text-center p-4 text-sm text-slate-400 space-y-2">
        <p>{t('blockchainSecured')} • {t('tamperProof')}</p>
        <div className="flex justify-center gap-4 text-xs">
          <a href="/privacy-policy" className="hover:text-[#0D2340] underline">
            Privacy Policy
          </a>
          <span>•</span>
          <a href="/terms" className="hover:text-[#0D2340] underline">
            Terms of Service
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
